import { z } from 'zod';
import { queryArray } from '../common/query-array';
import { MAX_SEARCH_QUERY_LENGTH, MIN_SEARCH_QUERY_LENGTH, SEARCH_ENTITY_ORDER } from './search.constants';

export const SearchEntityTypeSchema = z.enum(SEARCH_ENTITY_ORDER);

export const SearchPathParamsSchema = z.object({ workspaceId: z.uuid() });

// A query shorter than the minimum is a 400 rather than an empty result: the
// palette never sends one (it shows quick-nav instead), so a short query on the
// wire is a caller bug, not a user action.
export const SearchQuerySchema = z.object({
  // Capped as well as floored: `unaccent()` is not index-usable, so an
  // unbounded pattern is an unbounded sequential scan across five sources at
  // once. 200 matches the cap the mailbox search already uses.
  q: z.string().trim().min(MIN_SEARCH_QUERY_LENGTH).max(MAX_SEARCH_QUERY_LENGTH),
  /** Narrows the fan-out to these entity types; absent searches every source. */
  types: queryArray(SearchEntityTypeSchema).optional(),
});

export const SearchResultRowSchema = z.object({
  id: z.uuid(),
  /**
   * The record this row must be reached THROUGH, when its detail route is not
   * `/{entity}/{id}`. Null for every entity addressed by its own id; a Dokument
   * carries its Objekt's id, because documents have no route of their own.
   */
  parentId: z.uuid().nullable(),
  primary: z.string(),
  /** Locale-free factual text — an address, a company and role. Never a label. */
  secondary: z.string().nullable(),
  /**
   * An entity-specific enum token (`ACTIVE`, `IN_ACQUISITION`, …) the client
   * translates and appends to `secondary`. Labels are never composed
   * server-side: the backend localises only in the communications and document
   * generators (ADR 0009's `t()` boundary), and the German label belongs in the
   * dictionary keyed by this English token, never on the wire as the token
   * itself. Null when the entity's secondary line is purely factual.
   */
  secondaryToken: z.string().nullable(),
});

export const SearchResultGroupSchema = z.object({
  entityType: SearchEntityTypeSchema,
  rows: z.array(SearchResultRowSchema),
  /** The true match count before the per-group cap — drives „5 von 12". */
  total: z.number().int().nonnegative(),
});

/**
 * Groups arrive in `SEARCH_ENTITY_ORDER`. A group the caller may not read is
 * ABSENT; a group that was searched and matched nothing is present with no
 * rows. Collapsing the two would tell a member that a Standort holds no Objekte
 * when the truth is that they may not see Objekte at all.
 */
export const SearchResultsResponseSchema = z.object({
  groups: z.array(SearchResultGroupSchema),
});

export type SearchQuery = z.infer<typeof SearchQuerySchema>;
export type SearchResultRow = z.infer<typeof SearchResultRowSchema>;
export type SearchResultGroup = z.infer<typeof SearchResultGroupSchema>;
export type SearchResultsResponse = z.infer<typeof SearchResultsResponseSchema>;
